import * as React from 'react';
import { Expander } from 'sos-common/js/components/Expander/Expander';
import { renderFields, EMAIL_REGEX, validateFieldByRegex } from './org.admin.helpers';
import { Field } from '../StatsbidragGlobals/statsbidrag.types';
import { FormField } from 'sos-common/js/commonTypes/formField.types';

/**
 * These values are sent by server. See Socialstyrelsen\Business\Authorization\PermissionHelpers.cs
 */
export interface AddAuthorizationProps {
  canInviteRepresentant: boolean;
  canInviteSamordnare: boolean;
  postUrl: string;
}

export interface AddAuthorizationState {
  enableSendButton: boolean;
  fields: Field[];
  showForm: boolean;
}

/**
 * Invites new person to the organisation
 */
export class AddAuthorization extends React.Component<AddAuthorizationProps, AddAuthorizationState> {
  constructor(props: AddAuthorizationProps) {
    super(props);

    this.state = {
      enableSendButton: false,
      fields: this.defineFields(),
      showForm: false,
    };
  }

  validateRadioField = (fieldCopy: Field[]) => {
    fieldCopy.forEach((field) => {
      if (field.type === 'radio') {
        field.isValid = true;
        field.isChecked = false;
      }
    });
  };

  areAllFieldsValid = (fields: Field[]) => {
    const textFieldsValid = fields.find((x) => x.type !== 'radio' && x.mandatory && !x.isValid) === undefined;
    let radioIsValid = fields.find((x) => x.type === 'radio') === undefined;
    fields.forEach((field) => {
      if (field.type === 'radio' && field.isValid) {
        radioIsValid = true;
      }
    });
    return textFieldsValid && radioIsValid;
  };

  fieldChanged = (formfield: FormField<any>) => {
    const fieldCopy = this.state.fields.slice();
    let field = fieldCopy.find((x) => x.name === formfield.fieldName);

    if (formfield.fieldName === 'Inbjudan.Roll') {
      field = fieldCopy.find((x) => x.name === formfield.fieldName && x.value === formfield.value);
    }

    field.value = formfield.value;

    if (field.type === 'radio') {
      this.validateRadioField(fieldCopy);
      field.isChecked = true;
    } else {
      if (field.mandatory && !field.value) {
        field.isValid = false;
        field.errorMessage = 'Denna uppgift är obligatorisk att ange';
      } else if (field.validation && field.value) {
        field.isValid = validateFieldByRegex(field.validation, field.value);

        if (!field.isValid && field.name === 'Inbjudan.Epost') {
          field.errorMessage = 'Den angivna e-postadress stämmer inte med formatet abc@mail.se';
        }
      } else {
        field.isValid = true;
      }
    }

    const areAllFieldsValid = this.areAllFieldsValid(fieldCopy);

    this.setState({
      fields: fieldCopy,
      enableSendButton: areAllFieldsValid,
    });
  };

  /**
   * Fields that will be rendered to create a new person.
   */
  defineFields = () => {
    let fields = [
      {
        id: 'id_Inbjudan.Epost',
        type: 'text',
        label: 'E-postadress',
        name: 'Inbjudan.Epost',
        placeholder: 'abc@mail.se',
        validation: EMAIL_REGEX,
        mandatory: true,
        value: '',
        isValid: false,
        errorMessage: '',
      },
      {
        id: 'id_Inbjudan.Telefon',
        type: 'text',
        label: 'Telefon',
        name: 'Inbjudan.Telefonnummer',
        placeholder: '08-987 65 43',
        validation: '',
        mandatory: true,
        value: '',
        isValid: false,
      },
      {
        id: 'id_Inbjudan.Befattning',
        type: 'text',
        label: 'Befattning i organisationen',
        name: 'Inbjudan.Befattning',
        placeholder: 'Tjänstetitel',
        validation: '',
        mandatory: true,
        value: '',
        isValid: false,
      },
    ] as Field[];

    if (this.props.canInviteRepresentant) {
      fields.push({
        id: 'id_representant',
        type: 'radio',
        label: 'Representant',
        name: 'Inbjudan.Roll',
        placeholder: '',
        validation: '',
        mandatory: true,
        value: 'REPRESENTANT',
        isValid: false,
        radioHeading: 'Behörighet *',
      } as Field);
    }

    if (this.props.canInviteSamordnare) {
      fields.push({
        id: 'id_uppgiftssamordnare',
        type: 'radio',
        label: 'Uppgiftssamordnare',
        name: 'Inbjudan.Roll',
        placeholder: '',
        validation: '',
        mandatory: true,
        value: 'UPPGIFTSSAMORDNARE',
        isValid: false,
        radioHeading: 'Behörighet *',
      } as Field);
    }

    return fields;
  };

  setFormVisibility = (e: React.MouseEvent) => {
    e.preventDefault();
    this.setState({
      showForm: !this.state.showForm,
    });
  };

  public render() {
    const { enableSendButton, fields, showForm } = this.state;
    const { postUrl } = this.props;
    return (
      <div className="m-t-2">
        {!showForm && (
          <button onClick={this.setFormVisibility} className="button button--blue-plus-left">
            Lägg till
          </button>
        )}

        {showForm && (
          <form action={postUrl} method="POST">
            <div className="add-auth-wrapper">
              <h3 className="form-group-small">Lägg till en behörig person</h3>
              <p className="compact-text">
                Personen som du lägger till kommer få ett e-postmeddelande där de behöver besvara inbjudan genom att
                klicka på en länk. Personen loggar sedan in i tjänsten med BankID.
              </p>
              {fields && renderFields(fields, this.fieldChanged)}

              <input className="button button--blue" type="submit" disabled={!enableSendButton} value="Lägg till" />
              <input
                className="button button--white m-l-2"
                type="button"
                value="Avbryt"
                onClick={() => this.setState({ showForm: false })}
              />
            </div>
          </form>
        )}
      </div>
    );
  }
}
